/**
 * Flat 8-tile grid of every available field-takeoff calculator.
 * Replaces the old trade → category → template-calculator navigator —
 * these calculators are universal, not category-scoped.
 */

import React from 'react';
import { useIdioma } from '../../../i18n/idioma';
import { ChevronRight } from 'lucide-react';
import { CALCULATORS, CalculatorDef } from './registry';
import { StatusDot } from '../../ui/StatusDot';

interface CalculatorGridProps {
  onSelect: (id: string) => void;
  /** Accepted for call-site compatibility; no longer gates availability —
   *  every field calculator is always open for any trade/template. */
  activeTradeId?: string;
}

const CalculatorGrid: React.FC<CalculatorGridProps> = ({ onSelect }) => {
  // Field calculators are universal: always available, never locked by trade or
  // template. A contractor can run any takeoff math on any job.
  return (
    <div className="space-y-2">
      {CALCULATORS.map(c => (
        <CalculatorTile key={c.id} calc={c} onSelect={() => onSelect(c.id)} />
      ))}
    </div>
  );
};

const CalculatorTile: React.FC<{ calc: CalculatorDef; onSelect: () => void }> = ({ calc, onSelect }) => {
  const { t } = useIdioma();
  const { Icon } = calc;
  return (
    <button
      onClick={onSelect}
      className="w-full text-left border rounded-2xl p-4 flex items-start gap-3 transition-[border-color] duration-150 group bg-white border-acero-200 hover:border-acero-300 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
    >
      <div className="w-9 h-9 grid place-items-center bg-acero-100 text-acero-600 rounded-lg shrink-0 transition-colors duration-150 group-hover:text-navy-950">
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-navy-950 text-sm">{t(calc.name)}</span>
          {calc.badge === 'Daily'
            ? <StatusDot tone="marca"><span className="text-xs">{t('calc.badge.diario')}</span></StatusDot>
            : <span className="label-ui">{t('calc.badge.porTrabajo')}</span>}
        </div>
        <p className="text-[11px] text-acero-500 mt-1 leading-snug">{t(calc.description)}</p>
      </div>
      <ChevronRight size={16} className="text-acero-300 group-hover:text-navy-700 transition-colors duration-150 shrink-0 mt-1" />
    </button>
  );
};

export default CalculatorGrid;
